import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { AutoComplete } from 'antd';
import { CommonUtility, PalletStatusColors } from '../../utility';
import { PalletAutoCompleteHook, useAutocompleteLoading, useDebounce } from '../../hooks';
import { ColoredPill } from '../../elements';
import { AutocompleteSearchInput } from '../../components';

const CREATE_PREFIX = '__create__:';

const OptionContainer = styled.div`
  display: flex;
  flex: 1;
`;

function resultRenderer({ _id, palletNumber, status, search }) {
  const showResult = string => {
    try {
      const highlighted = string.replace(
        new RegExp(search, 'gi'),
        d => `<strong>${d}</strong>`,
      );

      return <span dangerouslySetInnerHTML={{ __html: highlighted }} />;
    } catch (error) {
      return string;
    }
  };

  return (
    <OptionContainer
      className="d-flex align-items-center justify-content-between"
      key={_id}
    >
      <span className="ml-2">{showResult(palletNumber)}</span>
      <ColoredPill mini color={PalletStatusColors[status]}>
        {CommonUtility.toTitleCase(status)}
      </ColoredPill>
    </OptionContainer>
  );
}

export function PalletSearchInput({
  top = undefined,
  onResultClick,
  inputClassName = undefined,
  setTableSearch = () => {},
  size = undefined,
  clearInput = false,
  refreshKey = undefined,
  warehouseId = undefined,
  defaultValue = '',
  resetDataKey = undefined,
  allowCreate: allowCreateProp = false,
}) {
  const [search, setSearch] = useState('');
  const [additionalOptions, setAdditionalOptions] = useState([]);

  const finalSearch = useDebounce(search, 700);
  const { data, loading, fetchData } = PalletAutoCompleteHook(warehouseId, finalSearch);
  const isSearching = useAutocompleteLoading(search, finalSearch, loading);

  useEffect(() => {
    setSearch(defaultValue);
  }, [defaultValue]);

  const mergedData = useMemo(() => {
    if (finalSearch !== search) return [];
    return [...(additionalOptions || []), ...(data || [])];
  }, [additionalOptions, data, finalSearch, search]);

  const showCreateOption =
    allowCreateProp &&
    !isSearching &&
    search === finalSearch &&
    CommonUtility.isNotEmpty(search) &&
    mergedData.length === 0;

  const options = useMemo(() => {
    const base = mergedData.map(item => ({
      value: item._id,
      label: resultRenderer({ ...item, search }),
    }));

    if (showCreateOption) {
      base.push({
        value: `${CREATE_PREFIX}${search.trim()}`,
        label: (
          <span className="auto-search-create-option">
            Add &quot;{search.trim()}&quot;
          </span>
        ),
      });
    }

    return base;
  }, [showCreateOption, mergedData, search]);

  const handleSelect = item => {
    if (!item?._id) {
      onResultClick({});
      setSearch('');
      setTableSearch('');
      return;
    }

    onResultClick(item);
    if (clearInput) {
      setSearch('');
    } else {
      setSearch(item.palletNumber);
    }
  };

  const handleCreate = value => {
    const newOption = {
      _id: value,
      palletNumber: value,
      status: 'available',
      newData: true,
    };
    setAdditionalOptions(prev => [newOption, ...(prev || [])]);
    onResultClick(newOption);
    if (clearInput) {
      setSearch('');
    } else {
      setSearch(value);
    }
  };

  const resetSearch = () => {
    setSearch('');
    setTableSearch('');
    onResultClick({});
  };

  useEffect(() => {
    if (CommonUtility.isNotEmpty(refreshKey)) {
      resetSearch();
    }
  }, [refreshKey]);

  useEffect(() => {
    if (CommonUtility.isNotEmpty(resetDataKey)) {
      fetchData();
    }
  }, [resetDataKey]);

  return (
    <AutoComplete
      className={inputClassName}
      value={search}
      options={options}
      filterOption={false}
      placeholder="Pallet Name"
      placement={top ? 'topLeft' : 'bottomLeft'}
      classNames={{ popup: { root: 'auto-search-dropdown' } }}
      onSearch={value => {
        setSearch(value);
        setTableSearch(value);
      }}
      onSelect={value => {
        if (String(value).startsWith(CREATE_PREFIX)) {
          handleCreate(String(value).slice(CREATE_PREFIX.length));
          return;
        }
        const item = mergedData.find(d => d._id === value);
        if (item) handleSelect(item);
      }}
    >
      <AutocompleteSearchInput
        size={size}
        isSearching={isSearching}
        onClear={resetSearch}
      />
    </AutoComplete>
  );
}
